import { useState, useEffect, useRef } from 'react';
import { FILMS } from '@/config/assets';
import { trackClick } from '@/lib/track';
import { trackFilmView, trackTrailerPlay, trackPlatformClick } from '@/lib/fanProfile';
import { X } from 'lucide-react';
import { NetflixLogo, PrimeLogo, PlayIcon, HotstarLogo } from '@/components/PlatformLogos';

function addUtm(url) {
  return url || '';
}

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^?&]+)/);
  return m ? m[1] : null;
}

export default function FilmWorlds({ onTrailer }) {
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach((e, i) => { if (e.isIntersecting) setTimeout(() => e.target.classList.add('visible'), i * 150); });
    }, { threshold: 0.1 });
    el.querySelectorAll('.reveal-el').forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, []);

  const openFilm = (film) => {
    setSelected(film);
    trackFilmView(film.title);
    trackClick({ film: film.title, platform: 'detail', action: 'open', section: 'film-worlds' });
  };

  return (
    <section id="film-worlds" data-testid="film-worlds" ref={ref} className="py-24 md:py-40 lg:py-56 px-5 md:px-10 lg:px-20 bg-now">
      <p className="reveal-el font-['Cormorant_Garamond'] text-xs tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--gold)' }}>Film Worlds</p>
      <h2 className="reveal-el font-['Playfair_Display'] italic text-4xl md:text-5xl lg:text-6xl font-normal" style={{ color: 'var(--warm-cream)' }}>The Experience</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-14 md:mt-20">
        {FILMS.map((film) => (
          <div
            key={film.title}
            data-testid={`film-card-${film.title.toLowerCase().replace(/[\s:]+/g, '-')}`}
            data-film={film.title}
            className="reveal-el film-card relative overflow-hidden rounded-sm cursor-pointer"
            style={{ aspectRatio: '2/3' }}
            onClick={() => openFilm(film)}
          >
            {/* Image or gold-gradient placeholder */}
            {film.image ? (
              <img src={film.image} alt={film.title} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center" style={{ background: 'linear-gradient(160deg, #1a1508 0%, #2a1f0a 40%, rgba(201,168,76,0.4) 100%)' }}>
                <p className="font-['Playfair_Display'] italic text-xl md:text-2xl font-normal leading-tight" style={{ color: 'var(--deep-black)' }}>{film.title}</p>
                {film.subtitle && <p className="font-['Inter'] text-[10px] mt-1" style={{ color: 'rgba(10,10,10,0.5)' }}>{film.subtitle}</p>}
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            {/* Award badge */}
            {film.award && (
              <div className="absolute top-3 left-3 px-2 py-1 rounded-sm" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.25)' }}>
                <p className="font-['Inter'] text-[8px] tracking-wider uppercase" style={{ color: 'var(--gold)' }}>{film.award}</p>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="font-['Playfair_Display'] italic text-sm md:text-base font-normal" style={{ color: 'var(--warm-cream)' }}>{film.title}</h3>
              {film.subtitle && <p className="font-['Inter'] text-[9px]" style={{ color: 'var(--ash)' }}>{film.subtitle}</p>}
              <div className="flex items-center gap-2 mt-1">
                <p className="font-['Inter'] text-[10px]" style={{ color: 'var(--ash)' }}>{film.year}</p>
                {film.role && <><span className="w-1 h-1 rounded-full" style={{ background: 'rgba(201,168,76,0.3)' }} /><p className="font-['Inter'] text-[10px]" style={{ color: 'var(--ash)' }}>as {film.role}</p></>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Film Detail Modal */}
      {selected && <FilmModal film={selected} onClose={() => setSelected(null)} onTrailer={onTrailer} />}
    </section>
  );
}

function FilmModal({ film, onClose, onTrailer }) {
  const [entered, setEntered] = useState(false);
  const videoId = extractYouTubeId(film.links?.trailer || film.url);
  const stills = film.stills || [];
  const galleryImages = film.gallery?.length ? film.gallery : [];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => setEntered(true));
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleClose = () => {
    setEntered(false);
    trackClick({ film: film.title, platform: 'detail', action: 'close', section: 'film-worlds' });
    setTimeout(onClose, 400);
  };

  const clickPlatform = (platform, url) => {
    trackPlatformClick(platform);
    trackClick({ film: film.title, platform, action: 'click', section: 'detail' });
    window.open(url, '_blank');
  };

  return (
    <div data-testid="film-modal" className="fixed inset-0 z-[150] flex items-start justify-center overflow-y-auto py-8 md:py-12" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.35s var(--ease-out-expo)' }}>
      {/* Backdrop with film mood tint */}
      <div className="fixed inset-0" style={{ background: film.mood || 'rgba(0,0,0,0.92)' }} onClick={handleClose} />

      {/* Close */}
      <button data-testid="film-modal-close" onClick={handleClose} className="fixed top-4 right-4 md:top-6 md:right-8 z-[170] p-2.5 rounded-full transition-all hover:bg-white/5 hover:scale-110" style={{ color: 'var(--gold)' }}>
        <X className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Modal frame */}
      <div
        className="relative z-10 w-[94vw] max-w-6xl rounded-md overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0C1222',
          border: '1px solid rgba(201,168,76,0.1)',
          boxShadow: '0 0 80px rgba(201,168,76,0.04), 0 0 120px rgba(0,0,0,0.6)',
          transform: entered ? 'scale(1)' : 'scale(0.96)',
          transition: 'transform 0.35s var(--ease-out-expo)',
        }}
      >
        {/* Film grain texture */}
        <div className="absolute inset-0 pointer-events-none z-[1] opacity-[0.02]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        {/* Warm radial glow behind poster area */}
        <div className="absolute left-0 top-0 w-[50%] h-full pointer-events-none z-[1]" style={{ background: 'radial-gradient(ellipse at 30% 40%, rgba(201,168,76,0.04) 0%, transparent 70%)' }} />

        {/* Two-column layout */}
        <div className="relative z-[2] flex flex-col sm:flex-row">
          {/* LEFT — Hero poster */}
          <div className="w-full sm:w-[35%] lg:w-[33%] flex-shrink-0" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.5s ease 0s' }}>
            <div className="relative w-full overflow-hidden group" style={{ aspectRatio: '2/3', background: '#0C1222' }}>
              {film.image ? (
                <img src={film.image} alt={film.title} className="w-full h-full object-contain transition-all duration-700 group-hover:scale-[1.03]" loading="eager" />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-6 text-center" style={{ background: 'linear-gradient(160deg, #1a1508 0%, #2a1f0a 40%, rgba(201,168,76,0.3) 100%)' }}>
                  <p className="font-['Playfair_Display'] italic text-2xl" style={{ color: 'var(--deep-black)' }}>{film.title}</p>
                </div>
              )}
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ boxShadow: 'inset 0 0 40px rgba(201,168,76,0.1)' }} />
            </div>
          </div>

          {/* RIGHT — Content with staggered reveals */}
          <div className="w-full sm:w-[65%] lg:w-[67%] p-6 md:p-8 lg:p-10 flex flex-col">
            {/* Title — stagger 0.1s */}
            <div style={{ opacity: entered ? 1 : 0, transform: entered ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.5s var(--ease-out-expo) 0.1s' }}>
              <h2 className="font-['Playfair_Display'] italic text-3xl md:text-4xl lg:text-5xl font-normal leading-tight" style={{ color: 'var(--warm-cream)' }}>
                {film.title}
              </h2>
              {film.subtitle && <p className="font-['Playfair_Display'] italic text-lg mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{film.subtitle}</p>}
            </div>

            {/* Meta chips — stagger 0.2s */}
            <div className="flex items-center flex-wrap gap-2 mt-5" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.5s ease 0.2s' }}>
              {[film.year, film.role && `as ${film.role}`, film.director && `Dir. ${film.director}`].filter(Boolean).map((chip, i) => (
                <span key={i} className="font-['Inter'] text-[11px] px-3 py-1 rounded-full" style={{ color: 'var(--warm-cream)', border: '1px solid rgba(201,168,76,0.12)', background: 'rgba(201,168,76,0.03)' }}>
                  {chip}
                </span>
              ))}
              {film.imdb && (
                <a href={film.imdb} target="_blank" rel="noopener noreferrer" className="font-['Inter'] text-[11px] px-3 py-1 rounded-full transition-all hover:bg-[rgba(201,168,76,0.08)]" style={{ color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.15)' }}>
                  IMDb
                </a>
              )}
            </div>

            {/* Award badge — gold glow */}
            {film.award && (
              <div className="inline-block mt-4 px-4 py-1.5 rounded-sm w-fit" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.5s ease 0.25s', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', boxShadow: '0 0 15px rgba(201,168,76,0.06)' }}>
                <p className="font-['Space_Mono'] text-[9px] tracking-wider uppercase" style={{ color: 'var(--gold)' }}>{film.award}</p>
              </div>
            )}

            {/* Synopsis with drop-cap — stagger 0.3s */}
            {film.description && (
              <p className="font-['Cormorant_Garamond'] italic text-base md:text-lg mt-6 leading-relaxed" style={{ color: 'rgba(245,240,232,0.45)', opacity: entered ? 1 : 0, transform: entered ? 'translateY(0)' : 'translateY(8px)', transition: 'all 0.5s var(--ease-out-expo) 0.3s' }}>
                <span className="font-['Playfair_Display'] text-3xl font-normal float-left mr-2 mt-1" style={{ color: 'var(--gold)', lineHeight: 0.8 }}>{film.description[0]}</span>
                {film.description.slice(1)}
              </p>
            )}

            {/* Thin gold rule */}
            <div className="h-px mt-8 mb-6" style={{ background: 'linear-gradient(to right, rgba(201,168,76,0.15), transparent)' }} />

            {/* Action bar — stagger 0.4s, hover lift */}
            <div className="flex flex-wrap gap-3" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.5s ease 0.4s' }}>
              {videoId && (
                <button data-testid="film-modal-trailer" data-film={film.title} onClick={() => onTrailer && onTrailer(film)} className="flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-['Inter'] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(201,168,76,0.12)]" style={{ border: '2px solid rgba(201,168,76,0.3)', color: 'var(--gold)' }}>
                  <PlayIcon className="w-5 h-5" />Watch Trailer
                </button>
              )}
              {film.links?.netflix && (
                <button data-testid="film-modal-netflix" data-film={film.title} onClick={() => clickPlatform('netflix', film.links.netflix)} className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-['Inter'] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(229,9,20,0.1)]" style={{ border: '1px solid rgba(229,9,20,0.25)', color: '#E50914' }}>
                  <NetflixLogo className="w-5 h-5" />Netflix
                </button>
              )}
              {film.links?.prime && (
                <button data-testid="film-modal-prime" data-film={film.title} onClick={() => clickPlatform('prime', film.links.prime)} className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-['Inter'] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,168,225,0.1)]" style={{ border: '1px solid rgba(0,168,225,0.25)', color: '#00A8E1' }}>
                  <PrimeLogo className="w-5 h-5" />Prime Video
                </button>
              )}
              {film.links?.bms && (
                <a href={film.links.bms} target="_blank" rel="noopener noreferrer" data-testid="film-modal-bms" data-film={film.title} onClick={() => { trackPlatformClick('bms'); trackClick({ film: film.title, platform: 'bms', action: 'click', section: 'detail' }); }} className="flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-['Inter'] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(201,168,76,0.15)]" style={{ background: 'var(--gold)', color: 'var(--deep-black)' }}>
                  Book Tickets
                </a>
              )}
              {film.links?.hotstar && (
                <button data-testid="film-modal-hotstar" data-film={film.title} onClick={() => clickPlatform('hotstar', film.links.hotstar)} className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-['Inter'] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,162,255,0.1)]" style={{ border: '1px solid rgba(0,162,255,0.25)', color: '#00A2FF' }}>
                  <HotstarLogo className="w-5 h-5" />JioHotstar
                </button>
              )}
            </div>

            {/* Stills gallery */}
            {(stills.length > 0 || galleryImages.length > 1) && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-8" style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.6s ease 0.5s' }}>
                {(stills.length > 0 ? stills : galleryImages.slice(1, 4)).map((img, i) => (
                  <div key={i} className="relative overflow-hidden rounded-sm" style={{ aspectRatio: '3/2' }}>
                    <img src={img} alt="" className="w-full h-full object-cover object-center" loading="lazy" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
